import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MortgageApplicationNavigationEvents } from '../../../../lib/constants/mortgage-application-navigation-events';
import { ReferenceDataLabels } from '../../../../lib/constants/reference-data-labels';
import { UserPermissions } from '../../../../lib/constants/user-permissions';
import { FormResponse, SectionDto } from '../../../../lib/models/dto';
import { ContextualHelpNotifierService } from '../../../../lib/services/contextual-help-notifier.service';
import { ReferenceDataService } from '../../../../lib/services/reference-data.service';
import { RomeNotificationService } from '../../../../lib/services/rome-notification.service';
import { TabNavigationService } from '../../../mortgage-application/services/tab-navigation.service';
import { BaseFormComponent } from '../../../shared/components/base-form.component';
import { Kfi } from '../../models/kfi.model';
import { KfiRouterService } from '../../services/kfi-router.service';
import { KfiStore } from '../../services/kfi-store';
import { KfiService } from '../../services/kfi.service';

@Component({
  selector: 'boi-kfi',
  templateUrl: './kfi.page.html',
  styleUrls: ['./../../../shared/styles/application.page.scss', './kfi.page.scss']
})
export class KfiPage extends BaseFormComponent implements OnInit, OnDestroy {
  public kfi: Kfi;
  public kfiId: string;
  public sections: { [key: string]: SectionDto };
  public activeTab: { key: string };

  public referenceDataLabels = ReferenceDataLabels;
  public userPermissions = UserPermissions;
  public mortgageApplicationNavigationEvents = MortgageApplicationNavigationEvents;
  public hasSubmitValidationError: boolean;

  private readonly destroySubject$ = new Subject();

  constructor(
    protected readonly activeRoute: ActivatedRoute,
    protected readonly kfiStore: KfiStore,
    protected readonly kfiService: KfiService,
    protected readonly kfiRouterService: KfiRouterService,
    protected readonly notificationsService: RomeNotificationService,
    protected readonly referenceDataService: ReferenceDataService,
    protected readonly contextualHelpNotifierService: ContextualHelpNotifierService,
    public router: Router,
    public tabNavigationService: TabNavigationService
  ) {
    super(router);
    this.tabNavigationService.event
      .pipe(filter((event) => event && event.type === MortgageApplicationNavigationEvents.ChangeMortgageAppTab))
      .subscribe((event) => {
        this.contextualHelpNotifierService.contextualHelpChanged(undefined);
        this.activeTab = {
          key: event.data['tab']
        };
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.tabNavigationService.emitEvent({
        type: MortgageApplicationNavigationEvents.ChangeMortgageAppTab,
        data: this.getTabNavigationDataFromUrl()
      });
    });
  }

  ngOnInit(): void {
    const resolver = this.activeRoute.snapshot.data['kfiDataResolver'];
    const kfiResponse = resolver['kfiForm'] as FormResponse<Kfi>;
    this.kfiId = this.activeRoute.snapshot.params['kfiId'];

    this.kfiStore.dataObservable.pipe(takeUntil(this.destroySubject$)).subscribe((data) => {
      this.kfi = data.Entity;
      this.sections = data.Sections;
      this.hasSubmitValidationError =
        data.hasSubmitValidationError !== undefined ? data.hasSubmitValidationError : this.hasSubmitValidationError;
    });

    this.kfiStore.kfiResponse = kfiResponse;
    this.kfiStore.kfiId = this.kfiId;

    this.kfiStore.setLoanTypes(resolver['allLoanTypes'].LoanTypes);
    this.kfiStore.mortgageClubs = resolver['mortgageClubs'];

    this.tabNavigationService.emitEvent({
      type: MortgageApplicationNavigationEvents.ChangeMortgageAppTab,
      data: this.getTabNavigationDataFromUrl()
    });
  }

  public submit(): void {
    this.kfiService
      .submitKfi(this.kfiService.prepareData(this.kfi))
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        (kfiResponse: FormResponse<Kfi>) => {
          if (kfiResponse) {
            const sections = this.kfiStore.kfiResponse.Sections;
            kfiResponse.IsSubmitted = true;
            kfiResponse.Sections = sections;
            if (kfiResponse.ValidationErrors.FullValidationErrors.length === 1) {
              kfiResponse.hasSubmitValidationError = false;
              this.kfiStore.kfiResponse = kfiResponse;
              this.kfiRouterService.redirectAfterSubmit(this.kfi.ApplicationCase.CaseNumberAsHash);
            } else {
              kfiResponse.hasSubmitValidationError = true;
              this.kfiStore.kfiResponse = kfiResponse;

              const localizedMessage = this.referenceDataService.getLocalizeLabel(
                this.referenceDataLabels.NotificationValidationErrors
              );
              this.notificationsService.showError(localizedMessage);
            }
          }
        },
        (error) => {}
      );
  }

  private destroyStoreData(): void {
    this.kfiStore.availableLoanTypes = undefined;
    this.kfiStore.kfiResponse.hasSubmitValidationError = undefined;
  }

  public ngOnDestroy(): void {
    this.destroyStoreData();
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
